import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { FaBed, FaBath, FaRulerCombined, FaPhone, FaEnvelope, FaMapMarkerAlt, FaTimes } from 'react-icons/fa';
import './PropertyDetail.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import ContactForm from './components/ContactForm/ContactForm';
import { Helmet } from 'react-helmet';

const PropertyDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentProperty, setCurrentProperty] = useState(null);
  const [activeTab, setActiveTab] = useState('description');

  // fullscreen modal states
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    setCurrentProperty(location.state?.propertyData || null);
  }, [location.state]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentProperty]);

  // Function to handle image click
  const handleImageClick = (index) => {
    setActiveImageIndex(index);
    setIsFullscreen(true);
  };

  // Function to format area display with unit
  const formatArea = (area) => {
    if (!area) return 'N/A';
    
    // Handle both old and new area formats
    if (typeof area === 'object' && area !== null) {
      // New format: { value: number, unit: string }
      return `${area.value || 'N/A'} ${area.unit || 'sqft'}`;
    } else {
      // Old format: number or string (backward compatibility)
      return `${area} sqft`;
    }
  };

  if (!currentProperty) {
    return (
      <div className="property-detail-page-container">
        <div className="property-error">
          <h2 className="property-error-title">Property Not Found</h2>
          <p>We couldn't find the property you're looking for.</p>
          <button 
            className="property-back-button"
            onClick={() => navigate(-1)}
          >
            Back to Properties
          </button>
        </div>
      </div>
    );
  }

  const baseURL = 'https://gladious-constructions.onrender.com'; 
  const images = currentProperty.images?.length > 0
    ? currentProperty.images.map((img) =>
        img.startsWith('http') ? img : `${baseURL}${img}`
      )
    : ['/images/logo.png'];

  const openGmail = (email) => {
    if (!email) {
      alert("❌ No email address provided.");
      return;
    }

    const title = currentProperty?.title || 'N/A';
    const price = currentProperty?.price ? `Rs ${currentProperty.price.toLocaleString()}` : 'N/A';
    
    // Use the formatArea function for the email
    const area = formatArea(currentProperty?.area);
    
    const location = [
      currentProperty?.location?.address,
      currentProperty?.location?.city,
      currentProperty?.location?.country,
    ]
      .filter(Boolean)
      .join(', ') || 'N/A';

    const subject = encodeURIComponent(`Inquiry about ${title} from Gladious`);
    const body = encodeURIComponent(
      `Hello,\n\nI am interested in the following property:\n\n` +
      `Title: ${title}\n` +
      `Price: ${price}\n` +
      `Area: ${area}\n` +
      `Location: ${location}\n\n` +
      `Please provide more details.\n\nThank you.`
    );

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile) {
      window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    } else {
      const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${subject}&body=${body}`;
      window.open(gmailUrl, '_blank');
    }
  };

  return (
    <>
      <Helmet>
        <title>Property Detail</title>
        <meta name="description" content="Welcome to Gladious – experts in sustainable and modern construction projects." />
      </Helmet>

      <div className="property-detail-page-container">
        {/* Hero Section with Swiper */}
        <div className="property-hero-section">
          <Swiper
            key={activeImageIndex} 
            modules={[Navigation, Pagination]}
            navigation
            pagination={{ clickable: true }}
            className="property-hero-swiper"
            initialSlide={activeImageIndex}
          >
            {images.map((img, index) => (
              <SwiperSlide key={index}>
                <div className="swiper-image-container">
                  <img
                    src={img}
                    alt={`${currentProperty.title} - ${index + 1}`}
                    className="property-hero-image"
                    loading='lazy'
                  />
                  {/* Add a transparent overlay for clicking */}
                  <div 
                    className="image-click-overlay"
                    onClick={() => handleImageClick(index)}
                  ></div>
                        <div className="property-hero-overlay">
                  <h1 className="property-hero-title">{currentProperty.title}</h1>
                  <p className="property-hero-price">
                    Rs {currentProperty.price?.toLocaleString() || 'Price on request'}
                  </p>
                </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>    

        {/* Fullscreen Modal */}
        {isFullscreen && (
          <div className="fullscreen-modal">
            <button className="close-btn" onClick={() => setIsFullscreen(false)}>
              <FaTimes size={28} />
            </button>
            <Swiper
              modules={[Navigation, Pagination]}
              navigation
              pagination={{ clickable: true }}
              initialSlide={activeImageIndex}
              className="fullscreen-swiper"
            >
              {images.map((img, index) => (
                <SwiperSlide key={index}>
                  <img 
                    src={img} 
                    alt={`Fullscreen ${index + 1}`} 
                    className="fullscreen-image"
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}


        <div className="property-main-content">
                  
          <div className="property-overview-section">
            <h2 className="property-overview-title">Property Overview</h2>
            <div className="property-key-features-section">
              <div className="property-feature">
                <FaBed />
                <div className="property-feature-details">
                  <span className="property-feature-value">
                    {currentProperty.bedrooms || 'N/A'}
                  </span>
                  <span className="property-feature-name">Bedrooms</span>
                </div>
              </div>
              <div className="property-feature">
                <FaBath />
                <div className="property-feature-details">
                  <span className="property-feature-value">
                    {currentProperty.bathrooms || 'N/A'}
                  </span>
                  <span className="property-feature-name">Bathrooms</span>
                </div>
              </div>
              <div className="property-feature">
                <FaRulerCombined />
                <div className="property-feature-details">
                  {/* Updated area display with unit */}
                  <span className="property-feature-value">
                    {formatArea(currentProperty.area)}
                  </span>
                  <span className="property-feature-name">Area</span>
                </div>
              </div>
              {currentProperty.features?.slice(0, 3).map((feature, index) => (
                <div className="property-feature" key={index}>
                  <span className="property-feature-icon">✓</span>
                  <div className="property-feature-details">
                    <span className="property-feature-value">{feature}</span>
                    <span className="property-feature-name">Feature</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="property-tabs-navigation">
            <div 
              className={`property-tab ${activeTab === 'description' ? 'active' : ''}`}
              onClick={() => setActiveTab('description')}
            >
              Description
            </div>
            <div 
              className={`property-tab ${activeTab === 'location' ? 'active' : ''}`}
              onClick={() => setActiveTab('location')}
            >
              Location
            </div>
          </div>

          {/* Tab Content */}
          <div className="property-tab-content">
            {activeTab === 'description' && (
              <div>
                <h3>Property Description</h3>
                <p>{currentProperty.description || 'No description available.'}</p>
              </div>
            )}
            {activeTab === 'location' && (
              <div>
                <h3>Property Location</h3>
                <p>
                  <FaMapMarkerAlt /> {currentProperty.location?.address || 'Address not specified.'}
                </p>
                <p>{currentProperty.location?.city}, {currentProperty.location?.country}</p>
              </div>
            )}
          </div>

          {/* Contact Section */}
          <div className="property-contact-section">
            <h2 className="property-contact-section-title">Interested in this property?</h2>
            <p className="property-contact-section-subtitle">
              Contact our agent to schedule a viewing or get more information about this property.
            </p>
            
            <div className="property-action-buttons">
              <a href={`tel:${currentProperty.phone || ''}`} className="property-action-button call">
                <FaPhone /> Call Agent
              </a>

              <button
                className="property-action-button email"
                onClick={() => openGmail(currentProperty.email)}
              >
                <FaEnvelope /> Email Agent
              </button>

              <button
                className="property-action-button contact"
                onClick={() => navigate('/contact')}
              >
                Schedule Viewing
              </button>
            </div>

            <ContactForm currentProperty={currentProperty} />
          </div>
        </div>
      </div>
    </>
  );
};

export default PropertyDetail;